import React, { useEffect, useState } from "react";
import { X, Globe2, Building2, Users, FileCheck2, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getPlatformOverview, updatePlatformTenant, formatApiError } from "@/auth";

const STATUSES = ["trialing", "active", "past_due", "canceled", "suspended"];

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" /> {label}</p>
    <p className="mt-1 font-display text-2xl font-bold text-slate-100">{value}</p>
  </div>
);

export default function PlatformModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => getPlatformOverview().then(setData).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;

  const change = async (t, patch) => {
    setBusy(t.id);
    try { await updatePlatformTenant(t.id, patch); toast.success(`${t.name} updated`); await load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="platform-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl border border-slate-700 bg-[#161f32] shadow-2xl fade-up">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-slate-100"><Globe2 className="h-5 w-5 text-orange-400" /> Platform — All Companies</h2>
          <button data-testid="platform-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5">
          {!data ? <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
            <>
              <div className="mb-5 grid gap-3 sm:grid-cols-4">
                <Kpi icon={Building2} label="Companies" value={data.totals.companies} testId="platform-kpi-companies" />
                <Kpi icon={Users} label="Active users" value={data.totals.users} testId="platform-kpi-users" />
                <Kpi icon={FileCheck2} label="Registered customers" value={data.totals.registered} testId="platform-kpi-registered" />
                <Kpi icon={DollarSign} label="Monthly recurring" value={`$${data.totals.mrr}`} testId="platform-kpi-mrr" />
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-800">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2">Company</th><th className="px-3 py-2">Owner</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Users</th><th className="px-3 py-2 text-right">Customers</th><th className="px-3 py-2 text-right">Registered</th><th className="px-3 py-2 text-right">This month</th><th className="px-3 py-2">Created</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {data.tenants.map((t) => (
                      <tr key={t.id} data-testid={`platform-row-${t.id}`} className="text-slate-200">
                        <td className="px-3 py-2 font-medium">{t.name}{t.note && <span className="ml-1 text-[10px] text-orange-400">(you)</span>}</td>
                        <td className="px-3 py-2 text-slate-400">{t.owner?.email}</td>
                        <td className="px-3 py-2">
                          <select data-testid={`platform-plan-${t.id}`} disabled={busy === t.id} value={t.plan} onChange={(e) => change(t, { plan: e.target.value })} className="rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs text-slate-100">
                            {Object.entries(data.plans).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select data-testid={`platform-status-${t.id}`} disabled={busy === t.id} value={t.plan_status} onChange={(e) => change(t, { plan_status: e.target.value })} className={`rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs ${t.plan_status === "active" ? "text-emerald-300" : t.plan_status === "suspended" || t.plan_status === "past_due" ? "text-rose-300" : "text-slate-300"}`}>
                            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right">{t.users} / {t.plan_limits.seats >= 999 ? "∞" : t.plan_limits.seats}</td>
                        <td className="px-3 py-2 text-right">{t.jobs}</td>
                        <td className="px-3 py-2 text-right">{t.registered}</td>
                        <td className="px-3 py-2 text-right">{t.registrations_this_month} / {t.plan_limits.monthly_registrations}</td>
                        <td className="px-3 py-2 text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[11px] text-slate-500">Changing a plan here overrides billing (use for complimentary or enterprise-contract accounts). Setting status to <b>suspended</b> blocks that company from registering until restored.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
